import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, 
  Clock, 
  DollarSign, 
  Calendar, 
  ExternalLink, 
  ArrowLeft,
  CheckCircle,
  User,
  Building
} from 'lucide-react';
import { Button, Card, CardContent, Badge, LoadingSpinner } from '../components/ui';
import { useJobStore } from '../store';
import { formatDate, formatDateTime } from '../utils';
import { fadeIn, slideInLeft } from '../utils/animations';

export const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentJob, isLoading, error, fetchJob, clearCurrentJob } = useJobStore();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      fetchJob(parseInt(id));
    }
    
    return () => {
      clearCurrentJob();
    };
  }, [id, fetchJob, clearCurrentJob]);

  useEffect(() => {
    if (currentJob && contentRef.current) {
      fadeIn(contentRef.current, 0.5);
    }
  }, [currentJob]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !currentJob) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-secondary-900 mb-4">
            Job Not Found
          </h2>
          <p className="text-secondary-600 mb-6">
            The job you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/jobs">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div ref={contentRef} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/jobs">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">
                      {currentJob.title}
                    </h1>
                    <div className="flex flex-wrap gap-4 text-secondary-600 mb-4">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4" />
                        <span>{currentJob.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-medium">{currentJob.pay}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4" />
                        <span>{currentJob.duration}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(currentJob.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <Badge variant={currentJob.is_active ? 'success' : 'secondary'}>
                    {currentJob.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {/* Company Info */}
                <div className="border-t border-secondary-200 pt-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Building className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm text-secondary-600">Company</p>
                      <p className="font-medium text-secondary-900">
                        {currentJob.company}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-secondary-900 mb-4">
                  Job Description
                </h2>
                <div className="prose prose-secondary max-w-none">
                  <p className="text-secondary-700 whitespace-pre-wrap">
                    {currentJob.description}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Requirements */}
            {currentJob.requirements && currentJob.requirements.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-semibold text-secondary-900 mb-4">
                    Requirements
                  </h2>
                  <div className="space-y-3">
                    {currentJob.requirements.map((requirement, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-secondary-700">{requirement}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card */}
            <Card className="sticky top-8">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Apply for this Job
                </h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Pay:</span>
                    <span className="font-medium text-secondary-900">{currentJob.pay}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Duration:</span>
                    <span className="font-medium text-secondary-900">{currentJob.duration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Category:</span>
                    <span className="font-medium text-secondary-900">{currentJob.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Posted:</span>
                    <span className="font-medium text-secondary-900">
                      {formatDate(currentJob.created_at)}
                    </span>
                  </div>
                </div>

                {currentJob.is_active ? (
                  <a
                    href={currentJob.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button size="lg" className="w-full">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Apply Now
                    </Button>
                  </a>
                ) : (
                  <Button size="lg" className="w-full" disabled>
                    Job No Longer Available
                  </Button>
                )}

                <p className="text-xs text-secondary-500 text-center mt-3">
                  You will be redirected to the application form
                </p>
              </CardContent>
            </Card>

            {/* Job Details */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  Job Details
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-secondary-600">Job ID:</span>
                    <span className="font-mono text-secondary-900 ml-2">#{currentJob.id}</span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Posted:</span>
                    <span className="text-secondary-900 ml-2">
                      {formatDateTime(currentJob.created_at)}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Company:</span>
                    <span className="text-secondary-900 ml-2">
                      {currentJob.company}
                    </span>
                  </div>
                  <div>
                    <span className="text-secondary-600">Status:</span>
                    <Badge 
                      variant={currentJob.is_active ? 'success' : 'secondary'}
                      className="ml-2"
                    >
                      {currentJob.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};